import cn from "@/v2-ui/utils/utils.cn"
import type {
  PublicCategoryStatus,
  PublicPlatform,
} from "@/v2-status-client/status/status.types"
import StatusSummaryRow from "@/v2-status-client/status/summary/StatusSummaryRow"
import StatusCategoryAccordion from "@/v2-status-client/status/category/StatusCategoryAccordion"
import StatusCategorySidebar from "@/v2-status-client/status/category/StatusCategorySidebar"
import { useJumpToCategory } from "@/v2-status-client/status/status.filter"

export type StatusPlatformDetailProps = {
  platform: PublicPlatform
  categories: PublicCategoryStatus[]
  // Category id encoded in the URL hash. Forwarded to the
  // accordion so the targeted row auto-expands on first render.
  activeCategoryId: string
}

// Per-platform "detail" view: rendered when the URL is scoped to a
// single platform (`?platform=…`). Three blocks:
//
//   1. Platform aggregate row at the top, standalone -- it's the
//      summary the customer is trying to read at a glance ("how is
//      Placepoint doing right now?") and shouldn't be buried inside
//      a card that also contains every category.
//
//   2. A two-column grid below: a left sidebar with the category
//      navigation (with a second tier for sub-categorised parents
//      like Map Layers) and a right column with the matching
//      collapsible category cards. The sidebar replaces the older
//      secondary horizontal pill row -- a vertical column reads
//      better when there are nested groupings to surface.
//
//   3. Categories rendered as individual collapsible accordion
//      cards. Each card's heading is sticky so it stays in view
//      while the customer scrolls through long service lists.
//
// The aggregate row stays a sibling (not an outer wrapper) of the
// grid so it can span the full width above both columns; trapping
// it inside the grid would make the platform summary fight for
// width with the sidebar for no reason.
function StatusPlatformDetail(props: Readonly<StatusPlatformDetailProps>) {
  const { platform, categories, activeCategoryId } = props
  const jumpToCategory = useJumpToCategory()
  return (
    <div
      id={`platform-${platform.id}`}
      className="flex flex-col gap-3 scroll-mt-24"
      aria-label={platform.name}
    >
      <StatusSummaryRow
        as="header"
        name={platform.name}
        description={platform.description}
        status={platform.status}
        summary={platform.summary}
        uptimePercent30d={platform.uptimePercent30d ?? null}
        className={cn(
          "rounded-lg border border-border/60 bg-background/30",
          "border-b px-4 py-3 sm:px-5",
        )}
      />
      {categories.length > 0 ? (
        <div
          className={cn(
            // Sidebar collapses below the cards on narrow viewports
            // -- a 200 px nav column squeezed into a phone leaves
            // 100 px for the content, which is unreadable. The
            // breakpoint matches Tailwind's `md` so it lines up
            // with the rest of the page's responsive switches.
            "grid gap-4 md:grid-cols-[200px_minmax(0,1fr)]",
          )}
        >
          <aside
            className={cn(
              // Sticky so the sidebar travels with the customer as
              // they scroll through long category bodies. `top` is
              // the platform pill row's height so the sidebar tucks
              // just under it. `h-fit` + `align-self: start` (set
              // implicitly via the grid's first row alignment) keep
              // the sidebar from stretching to fill the grid track.
              "md:sticky md:top-16 h-fit",
            )}
          >
            <StatusCategorySidebar
              categories={categories}
              activeCategoryId={activeCategoryId}
              onSelect={jumpToCategory}
            />
          </aside>
          <div className="min-w-0">
            <StatusCategoryAccordion
              categories={categories}
              activeCategoryId={activeCategoryId}
            />
          </div>
        </div>
      ) : (
        <p
          className={cn(
            "rounded-md border border-border/60 bg-background/30",
            "px-4 py-3 text-xs text-muted-foreground sm:px-5",
          )}
        >
          No services configured yet.
        </p>
      )}
    </div>
  )
}

export default StatusPlatformDetail
